import React, { FunctionComponent } from 'react'
import { Button, Heading, IconAvatar, Link, Skeleton, Text, useMoney } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import { useAppDispatch, useAppRouter, useGetCreditLimitQuery } from '../../../state'
import { setWelcomeWithInitialLimitSeen } from '../../../state/registration/reducer'
import { useConsumerSizes } from '../../../utils/responsive'
import styles from './style.module.scss'

const I18N_NAMESPACE = ['welcomeWithInitialLimit', 'common']

export interface WelcomeWithInitialLimitModalProps {
  isOpen: boolean
}

export const WelcomeWithInitialLimitModal: FunctionComponent<WelcomeWithInitialLimitModalProps> = ({ isOpen }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { headingSize, textSize } = useConsumerSizes()
  const { data: creditLimitData, isLoading } = useGetCreditLimitQuery()
  const { formatMoney } = useMoney({
    locale: router.locale,
    currency: creditLimitData?.creditLimit?.currency,
  })

  if (!isOpen) {
    return null
  }

  const handleContinue = (): void => {
    dispatch(setWelcomeWithInitialLimitSeen())
  }

  return (
    <section className={styles.initialLimitPage} aria-labelledby='welcome-initial-limit-heading' data-testid='welcome-initial-limit-page'>
      <div className={styles.initialLimitViewport}>
        <div className={styles.initialLimitContent}>
          <div className={styles.initialLimitIcon}>
            <IconAvatar iconName='Check' backgroundColor='Bondi Mint' size='L' testNameSpace='welcome-check-icon' />
          </div>
          <div className={styles.initialLimitHeading}>
            <Heading id='welcome-initial-limit-heading' renderAs='h1' size={headingSize}>
              {t('welcomeWithInitialLimit:heading')}
            </Heading>
          </div>
          <div className={styles.initialLimitSubtitle}>
            <Text size={textSize} color='Subtle'>
              {t('welcomeWithInitialLimit:estimatedSpend')}
            </Text>
          </div>
          <div className={styles.initialLimitAmount}>
            {isLoading ? (
              <Skeleton kind='Default' />
            ) : (
              creditLimitData?.creditLimit != null && <Heading size='XXXXL'>{formatMoney(creditLimitData.creditLimit.amount)}</Heading>
            )}
          </div>
          <div className={styles.initialLimitSections}>
            <div className={styles.initialLimitSection}>
              <Text size={textSize} bold>
                {t('welcomeWithInitialLimit:uniqueAmountTitle')}
              </Text>
              <Text size={textSize} color='Subtle'>
                <Trans
                  i18nKey='welcomeWithInitialLimit:uniqueAmountBody'
                  components={{
                    clickHereLink: <Link kind='Underline' href={t('common:spendLimitInfoUrl')} target='_blank' />,
                  }}
                />
              </Text>
            </div>
            <div className={styles.initialLimitSection}>
              <Text size={textSize} bold>
                {t('welcomeWithInitialLimit:justSoYouKnowTitle')}
              </Text>
              <Text size={textSize} color='Subtle'>
                {t('welcomeWithInitialLimit:justSoYouKnowBody')}
              </Text>
            </div>
          </div>
          <div className={styles.initialLimitCta}>
            <Button.Primary size='Large' padding='Fluid' onClick={handleContinue} testNameSpace='welcome-initial-limit-continue'>
              {t('welcomeWithInitialLimit:continue')}
            </Button.Primary>
          </div>
        </div>
      </div>
    </section>
  )
}
